var moment = require('moment-timezone');

var SMALL_NUMBER_WORDS = {
  0: 'Zero',
  1: 'One',
  2: 'Two',
  3: 'Three',
  4: 'Four',
  5: 'Five',
  6: 'Six',
  7: 'Seven',
  8: 'Eight',
  9: 'Nine',
  10: 'Ten',
  11: 'Eleven',
  12: 'Twelve'
};

function spellCount(count) {
  if (SMALL_NUMBER_WORDS[count]) return SMALL_NUMBER_WORDS[count];
  return String(count);
}

function formatCountWithUnit(count, singular, plural) {
  return spellCount(count) + ' ' + (count === 1 ? singular : plural);
}

function getLocalTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  } catch (e) {
    return 'America/New_York';
  }
}

function buildRelativeEventTimeLabel(timestamp, referenceMoment) {
  if (timestamp == null || timestamp === '') return '';

  var eventMoment = moment(Number(timestamp));
  if (!eventMoment.isValid()) {
    eventMoment = moment(timestamp);
  }
  if (!eventMoment.isValid()) return '';

  var now = moment.isMoment(referenceMoment) ? referenceMoment.clone() : moment();
  var startOfToday = now.clone().startOf('day');
  var startOfEventDay = eventMoment.clone().startOf('day');
  var dayDiff = startOfToday.diff(startOfEventDay, 'days');

  if (dayDiff === 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff > 1 && dayDiff < 7) return formatCountWithUnit(dayDiff, 'day', 'days') + ' ago';
  if (dayDiff >= 7 && dayDiff < 14) return 'Last week';
  if (dayDiff >= 14 && dayDiff < 30) return formatCountWithUnit(Math.floor(dayDiff / 7), 'week', 'weeks') + ' ago';
  if (dayDiff >= 30 && dayDiff < 60) return 'Last month';
  if (dayDiff >= 60 && dayDiff < 365) return formatCountWithUnit(Math.floor(dayDiff / 30), 'month', 'months') + ' ago';
  if (dayDiff >= 365 && dayDiff < 730) return 'Last year';
  if (dayDiff >= 730) return formatCountWithUnit(Math.floor(dayDiff / 365), 'year', 'years') + ' ago';

  if (dayDiff === -1) return 'Tomorrow';
  if (dayDiff < -1 && dayDiff > -7) return 'In ' + formatCountWithUnit(Math.abs(dayDiff), 'day', 'days');
  if (dayDiff <= -7 && dayDiff > -14) return 'Next week';
  if (dayDiff <= -14 && dayDiff > -30) return 'In ' + formatCountWithUnit(Math.floor(Math.abs(dayDiff) / 7), 'week', 'weeks');
  if (dayDiff <= -30 && dayDiff > -60) return 'Next month';
  if (dayDiff <= -60 && dayDiff > -365) return 'In ' + formatCountWithUnit(Math.floor(Math.abs(dayDiff) / 30), 'month', 'months');
  if (dayDiff <= -365 && dayDiff > -730) return 'Next year';
  return 'In ' + formatCountWithUnit(Math.floor(Math.abs(dayDiff) / 365), 'year', 'years');
}

function parseEventTimestamp(value) {
  if (value == null || value === '') return null;
  var numericValue = Number(value);
  if (Number.isFinite(numericValue)) return numericValue;
  var parsedMoment = moment(value);
  if (!parsedMoment.isValid()) return null;
  return parsedMoment.valueOf();
}

function buildDurationLabel(startValue, endValue) {
  var start = parseEventTimestamp(startValue);
  var end = parseEventTimestamp(endValue);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return '';

  var totalSeconds = Math.floor((end - start) / 1000);
  var days = Math.floor(totalSeconds / 86400);
  var hours = Math.floor((totalSeconds % 86400) / 3600);
  var minutes = Math.floor((totalSeconds % 3600) / 60);
  var seconds = totalSeconds % 60;
  var parts = [];

  if (days > 0) parts.push(days + 'd');
  if (hours > 0) parts.push(hours + 'h');
  if (minutes > 0) parts.push(minutes + 'm');
  if (seconds > 0 || !parts.length) parts.push(seconds + 's');

  return parts.slice(0, 2).join(' ');
}

function formatEventDate(value, timezone) {
  if (!value) return 'Active';
  return moment(value).tz(timezone || getLocalTimezone()).format('M/D/YYYY, h:mm:ss a');
}

function getEventReportName(eventRecord) {
  if (eventRecord && eventRecord.reportName) return eventRecord.reportName;
  if (
    eventRecord &&
    eventRecord.out_OccurredAt &&
    eventRecord.out_OccurredAt[0] &&
    eventRecord.out_OccurredAt[0].in &&
    eventRecord.out_OccurredAt[0].in.name
  ) {
    return eventRecord.out_OccurredAt[0].in.name;
  }
  return '';
}

function getEventLocationPath(eventRecord) {
  if (eventRecord && eventRecord.reportLocationPath) {
    return eventRecord.reportLocationPath;
  }
  return '';
}

function formatLocationSegment(record) {
  if (!record || typeof record !== 'object') return '';
  if (record.name === 'root') return 'Home';
  var baseName = record.name || record['@class'] || '';
  if (record.roomNumber) {
    return baseName ? (baseName + ' ' + record.roomNumber) : String(record.roomNumber);
  }
  return baseName;
}

function getContextLocationSegments(contextLocation) {
  var record = typeof contextLocation === 'function' ? contextLocation() : contextLocation;
  if (!record) return [];

  var path = Array.isArray(record.path) ? record.path.slice() : [];
  if (!path.length && record['@rid']) {
    path = [record];
  }

  return path
    .map(formatLocationSegment)
    .filter(Boolean)
    .filter(function (segment) {
      return segment !== 'Home' && segment !== 'root';
    });
}

function getDisplayEventLocationPath(eventRecord, contextLocation) {
  var fullPath = getEventLocationPath(eventRecord);
  if (!fullPath) return '';

  var fullSegments = String(fullPath)
    .split(' / ')
    .map(function (segment) { return String(segment || '').trim(); })
    .filter(Boolean);

  var contextSegments = getContextLocationSegments(contextLocation);
  if (!contextSegments.length) {
    return fullSegments.join(' / ');
  }

  var matchLength = 0;
  while (
    matchLength < contextSegments.length &&
    matchLength < fullSegments.length &&
    contextSegments[matchLength] === fullSegments[matchLength]
  ) {
    matchLength += 1;
  }

  var relativeSegments = fullSegments.slice(matchLength);
  if (!relativeSegments.length) {
    return contextSegments[contextSegments.length - 1] || '';
  }
  return relativeSegments.join(' / ');
}

module.exports = {
  buildRelativeEventTimeLabel: buildRelativeEventTimeLabel,
  buildDurationLabel: buildDurationLabel,
  formatEventDate: formatEventDate,
  getEventReportName: getEventReportName,
  getEventLocationPath: getEventLocationPath,
  getDisplayEventLocationPath: getDisplayEventLocationPath,
  getLocalTimezone: getLocalTimezone
};
